/**
 * Project Scheduler — single source of truth for brand, typography, surfaces, and data colors.
 *
 * When official FalconBrick brand guidelines are available, update values here only.
 * For a single primary hex, replace `primaryShades` (use https://mantine.dev/colors-generator/
 * to generate a 10-shade tuple) or swap the whole array.
 */

/** Mantine requires exactly 10 shades per custom color (index 6 = default primary). */
export type MantineColorTuple = [
  string,
  string,
  string,
  string,
  string,
  string,
  string,
  string,
  string,
  string,
];

export const APP_BRAND = {
  /** Display name (header, docs). */
  productName: 'FalconBrick Planner',

  /**
   * Primary brand palette (placeholder: indigo).
   * Index 6 is the main button / active nav color.
   */
  primaryShades: [
    '#eef2ff',
    '#e0e7ff',
    '#c7d2fe',
    '#a5b4fc',
    '#818cf8',
    '#6366f1',
    '#4f46e5',
    '#4338ca',
    '#3730a3',
    '#312e81',
  ] as MantineColorTuple,

  fonts: {
    sans: 'Inter, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
    mono: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", monospace',
  },

  /** Layout surfaces (chrome, not chart data). */
  surfaces: {
    light: {
      appShell: '#f1f3f5',
      toolbar: '#f1f5f9',
      drawer: '#f8fafc',
      card: '#ffffff',
      disabledAction: '#94a3b8',
    },
    dark: {
      appShell: '#141517',
      toolbar: '#1a1b1e',
      drawer: '#25262b',
      card: '#1a1b1e',
      disabledAction: '#5c6370',
    },
  },

  text: {
    light: {
      muted: '#64748b',
      info: '#4f46e5',
    },
    dark: {
      muted: '#909296',
      info: '#a5b4fc',
    },
  },

  /** Header tabs + navigation drawer menu items. */
  nav: {
    light: {
      active: '#4338ca',
      activeBorder: '#4f46e5',
      inactive: '#64748b',
      hoverText: '#3730a3',
      menuHoverBg: '#eef2ff',
      menuHoverBorder: '#a5b4fc',
    },
    dark: {
      active: '#c7d2fe',
      activeBorder: '#a5b4fc',
      inactive: '#ced4da',
      hoverText: '#e0e7ff',
      menuHoverBg: '#2c2e33',
      menuHoverBorder: '#5c6bc0',
    },
  },

  /** AI review / highlight panels (location management). */
  aiReview: {
    light: { background: '#f8f8ff' },
    dark: { background: '#1e1b4b' },
  },

  /** Dashboard chart segment colors (readable in both modes). */
  dashboard: {
    light: {
      notStarted: '#adb5bd',
      inProgress: '#fab005',
      completed: '#2f9e44',
    },
    dark: {
      notStarted: '#868e96',
      inProgress: '#fcc419',
      completed: '#51cf66',
    },
  },

  /**
   * Gantt chart semantics (task bars, links). Independent from UI chrome so schedules stay readable.
   */
  gantt: {
    location: { bg: '#7c3aed', border: '#6d28d9' },
    activity: { bg: '#e64980', border: '#c2255c' },
    completed: { bg: '#40c057', border: '#2f9e44' },
    critical: { border: '#fa5252', shadow: '#ffc9c9' },
    links: {
      finishStart: '#339af0',
      startStart: '#22b8cf',
      finishFinish: '#845ef7',
    },
  },
} as const;
